import { StateUpdater, useEffect, useRef, useState } from 'preact/hooks';
import { useCountdown, useOnClickOutside } from 'usehooks-ts';
import { Button } from '@ts/components/showcase-ui/Button/Button';
import { allowedTime, isCommentAllowed } from './comment';
import { InfoBanner } from '../InfoBanner/InfoBanner';
import { Icon } from '../Icon/Icon';

type Props = {
  setIsModalOpen: StateUpdater<boolean>;
  comment: string;
  confirmButton: HTMLElement;
};

export const CommentModal = ({
  setIsModalOpen,
  comment,
  confirmButton,
}: Props) => {
  const countDownInitialValue = 5;
  const [timeLeft] = useState<number>(countDownInitialValue);
  const [count, { startCountdown }] = useCountdown({ countStart: timeLeft });

  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);

  const article = document.querySelector<HTMLElement>(
    '.bv-skeleton--article-page',
  );

  const openModal = () => {
    if (!modalRef.current) return;
    modalRef.current.style.display = 'block';

    // Fix page scroll when modal is open, so only modal is able scrolls
    // fix scrollbar width to avoid layout shift to the right
    document.body.classList.add('modal-open-no-scroll');
    if (article) article.style.overflowY = 'scroll';
  };

  const countDownToComment = () => {
    startCountdown();

    const timerId = setTimeout(() => {
      setIsConfirmButtonDisabled(() => false);
    }, countDownInitialValue * 1000);

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  };

  const insertComment = () => {
    if (!commentRef.current) return;
    commentRef.current.innerHTML = comment;
  };

  const handleConfirm = () => {
    // Is comment allowed by time
    if (!isCommentAllowed()) {
      setIsConfirmButtonDisabled(() => true);
      setShowBanner(() => true);
    } else if (!isConfirmButtonDisabled && !showBanner) confirmButton.click();
  };

  const handleCloseModal = () => {
    if (!modalRef.current) return;
    modalRef.current.style.display = 'none';

    // Remove fix page scroll when modal is closed
    // Remove fix on scrollbar width
    document.body.classList.remove('modal-open-no-scroll');
    if (article) article.style.overflowY = '';

    setIsModalOpen(() => false);
    setIsConfirmButtonDisabled(() => true);
  };

  const closeModalOnEscape = () => {
    // Close modal if `Esc` key is pressed
    document.addEventListener('keydown', evt => {
      if (evt.key === 'Escape' || evt.key === 'Esc') handleCloseModal();
    });
  };

  useOnClickOutside(modalBodyRef, handleCloseModal);

  useEffect(() => {
    openModal();
    countDownToComment();
    insertComment();

    closeModalOnEscape();
  }, []);

  return (
    <div className="bc-comment-modal" ref={modalRef}>
      <div className="bc-comment-modal__body" ref={modalBodyRef}>
        <div className="bc-comment-modal__title">
          <h2>Ihr Kommentar</h2>

          <div onClick={handleCloseModal} style={{ cursor: 'pointer' }}>
            <Icon iconName="cross" width={24} height={24} />
          </div>
        </div>

        <div className="bc-comment-modal__text">
          <div className="bc-comment-modal__content" ref={commentRef}></div>
        </div>

        {!isCommentAllowed() && (
          <InfoBanner
            type="error"
            content={`Die Kommentarfunktion ist derzeit geschlossen. Sie können jeden Tag zwischen ${allowedTime.startTime}:00 und ${allowedTime.endTime}:00 kommentieren.`}
            marginBottom="var(--DF-PRIM-SPACING-24)"
          />
        )}

        <div
          className="bc-comment-modal__buttons"
          style={{
            marginTop: `${commentRef.current && commentRef.current.offsetHeight < 320 ? '0' : 'var(--modal-spacing-gap)'}`,
          }}
        >
          <div onClick={handleConfirm}>
            <Button
              type="button"
              label="Kommentieren"
              className={`bo-btn--md${
                isConfirmButtonDisabled || !isCommentAllowed()
                  ? ' bo-btn--disabled-full'
                  : ''
              }`}
            />
          </div>
          <div onClick={handleCloseModal}>
            <Button
              type="button"
              label="Überarbeiten"
              className={`bo-btn--md bo-btn--secondary`}
            />
          </div>
        </div>

        {isConfirmButtonDisabled && isCommentAllowed() && (
          <div style={{ marginTop: 'var(--DF-PRIM-SPACING-16)' }}>
            Sie können Ihren Kommentar in {count} Sek. posten.
          </div>
        )}
      </div>
    </div>
  );
};
